import { useEffect, useState, useMemo } from "react";
import productsData from "../../data/products.json";
import { generateInvoiceNumber } from "../utils/invoiceNumber";

import {
  calculateItem,
  calculateTotals
} from "../utils/calculations";

import ProductSelector from "../../components/ProductSelector";
import InvoicePreview from "./InvoicePreview";

export default function IndianInvoiceForm() {

  /* ---------------- DOCUMENT TYPE ---------------- */

  // invoice | Proforma | receipt
  const [documentType, setDocumentType] =
    useState("invoice");

  /* ---------------- STATES ---------------- */

  const [invoiceNo, setInvoiceNo] = useState("");
  const [prnNo, setPrnNo] = useState("");

  // igst | gst
  const [taxMode, setTaxMode] =
    useState("igst");

  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "India",
    gst: "",
    pan: ""
  });

  const [payment, setPayment] = useState({
    additionalInfo: [],
    paymentDetails: [],
  });

  const [items, setItems] = useState([]);

  const [totals, setTotals] = useState({
    amount: 0,
    discount: 0,
    taxableAmount: 0,

    igst: 0,
    cgst: 0,
    sgst: 0,

    total: 0
  });

  /* ---------------- AUTO NUMBER ---------------- */

  useEffect(() => {

    const generatedNumber =
      generateInvoiceNumber(documentType);

    setInvoiceNo(generatedNumber);

  }, [documentType]);

  /* ---------------- ITEMS ---------------- */

  const calculatedItems = useMemo(() => {

    return items.map((item) => ({
      ...item,
      ...calculateItem(item)
    }));

  }, [items]);

  /* ---------------- TOTALS ---------------- */

  useEffect(() => {

    const baseTotals =
      calculateTotals(calculatedItems);

    if (taxMode === "igst") {

      setTotals({
        ...baseTotals,
        cgst: 0,
        sgst: 0,
      });

    } else {

      const cgst = Math.round(baseTotals.igst / 2);
      const sgst = baseTotals.igst - cgst;

      setTotals({
        ...baseTotals,
        igst: 0,
        cgst,
        sgst,
      });

    }

  }, [calculatedItems, taxMode]);

  /* ---------------- ADDITIONAL INFO ---------------- */

  const updateAdditional = (
    index,
    field,
    val
  ) => {

    const updated = [
      ...payment.additionalInfo
    ];

    updated[index][field] = val;

    setPayment({
      ...payment,
      additionalInfo: updated
    });

  };

  const addAdditionalRow = () => {

    setPayment({
      ...payment,
      additionalInfo: [
        ...payment.additionalInfo,
        { key: "", value: "" }
      ]
    });

  };

  const removeAdditionalRow = (index) => {

    const updated =
      payment.additionalInfo.filter(
        (_, i) => i !== index
      );

    setPayment({
      ...payment,
      additionalInfo: updated
    });

  };

  /* ---------------- PAYMENT DETAILS ---------------- */

  const updatePaymentDetail = (index, field, val) => {
    setPayment((prev) => {
      const updated = [...prev.paymentDetails];
      updated[index] = { ...updated[index], [field]: val };
      return { ...prev, paymentDetails: updated };
    });
  };

  const addPaymentDetailRow = () => {
    setPayment((prev) => ({
      ...prev,
      paymentDetails: [
        ...prev.paymentDetails,
        { description: "", amount: "", date: "", reference: "" },
      ],
    }));
  };

  const removePaymentDetailRow = (index) => {
    setPayment((prev) => ({
      ...prev,
      paymentDetails: prev.paymentDetails.filter((_, i) => i !== index),
    }));
  };

  /* ---------------- LABELS ---------------- */

  const documentLabels = {
    invoice: "Invoice",
    proforma: "Proforma Invoice",
    receipt: "Receipt"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 p-3 sm:p-5 lg:p-8">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}

        <div className="mb-6 sm:mb-8">

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
            SPORTSNTW Document Generator
          </h1>

          <p className="text-slate-500 mt-2 text-sm sm:text-base">
            Generate invoices, receipts & Proforma invoices
          </p>

        </div>

        {/* MAIN GRID */}

        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">

          {/* LEFT */}

          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-4 sm:p-6">

            {/* DOCUMENT TYPE */}

            <div className="mb-8">

              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5">

                <div className="mb-5">

                  <h2 className="text-lg font-semibold text-slate-800">
                    Document Type
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Select what you want to generate
                  </p>

                </div>

                {/* TOGGLES */}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                  {[
                    {
                      key: "invoice",
                      label: "Invoice"
                    },
                    {
                      key: "proforma",
                      label: "Proforma Invoice"
                    },
                    {
                      key: "receipt",
                      label: "Receipt"
                    }
                  ].map((type) => (

                    <button
                      key={type.key}
                      onClick={() =>
                        setDocumentType(type.key)
                      }
                      className={`rounded-2xl px-4 py-4 text-sm font-semibold transition-all duration-300 border ${
                        documentType === type.key
                          ? "bg-purple-600 text-white border-purple-600 shadow-lg"
                          : "bg-white border-slate-200 text-slate-700 hover:border-purple-300"
                      }`}
                    >
                      {type.label}
                    </button>

                  ))}

                </div>

              </div>

            </div>

            {/* INVOICE INFO */}

            <div className="mb-8">

              <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-5">

                {documentLabels[documentType]} Information

              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* NUMBER */}

                <div>

                  <label className="block text-sm font-medium text-slate-600 mb-2">

                    {documentType === "receipt"
                      ? "Receipt Number"
                      : documentType === "proforma"
                        ? "Proforma Invoice Number"
                        : "Invoice Number"}

                  </label>

                  <input
                    type="text"
                    value={invoiceNo}
                    onChange={(e) =>
                      setInvoiceNo(
                        e.target.value
                      )
                    }
                    className="w-full border border-slate-300 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                </div>

                {/* DATE */}

                <div>

                  <label className="block text-sm font-medium text-slate-600 mb-2">

                    Date

                  </label>

                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) =>
                      setInvoiceDate(e.target.value)
                    }
                    className="w-full border border-slate-300 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                </div>

                {/* PRN */}

                <div className="md:col-span-2">

                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    PRN Number
                  </label>

                  <input
                    type="text"
                    value={prnNo}
                    onChange={(e) =>
                      setPrnNo(e.target.value)
                    }
                    placeholder="Enter PRN Number"
                    className="w-full border border-slate-300 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                </div>

              </div>

            </div>

            {/* TAX MODE */}

            <div className="mb-8">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50 border border-slate-200 rounded-3xl p-5">

                <div>

                  <h2 className="text-lg font-semibold text-slate-800">
                    Tax Mode
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    CGST + SGST For Haryana | IGST For Other States
                  </p>

                </div>

                {/* TOGGLE */}

                <div className="flex items-center gap-4">

                  <span
                    className={`text-sm font-medium ${
                      taxMode === "igst"
                        ? "text-blue-600"
                        : "text-slate-400"
                    }`}
                  >
                    IGST
                  </span>

                  <button
                    onClick={() =>
                      setTaxMode(
                        taxMode === "igst"
                          ? "gst"
                          : "igst"
                      )
                    }
                    className={`relative w-16 h-9 rounded-full transition ${
                      taxMode === "gst"
                        ? "bg-green-500"
                        : "bg-blue-500"
                    }`}
                  >

                    <div
                      className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-md transition-all duration-300 ${
                        taxMode === "gst"
                          ? "left-8"
                          : "left-1"
                      }`}
                    />

                  </button>

                  <span
                    className={`text-sm font-medium ${
                      taxMode === "gst"
                        ? "text-green-600"
                        : "text-slate-400"
                    }`}
                  >
                    CGST + SGST
                  </span>

                </div>

              </div>

            </div>

            {/* CUSTOMER */}

            <div className="mb-8">

              <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-5">
                Customer Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {[
                  [
                    "name",
                    "Customer / Business Name",
                    "text"
                  ],
                  ["email", "Email", "email"],
                  ["phone", "Phone", "tel"],
                  ["address", "Address", "text"],
                  [
                    "gst",
                    "GST Number (optional)",
                    "text"
                  ],
                  [
                    "pan",
                    "PAN Number (optional)",
                    "text"
                  ]
                ].map(([key, label, type]) => (

                  <div
                    key={key}
                    className={
                      key === "address"
                        ? "md:col-span-2"
                        : ""
                    }
                  >

                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {label}
                    </label>

                    <input
                      type={type}
                      placeholder={label}
                      value={customer[key]}
                      onChange={(e) =>
                        setCustomer({
                          ...customer,
                          [key]:
                            e.target.value
                        })
                      }
                      className="w-full border border-slate-300 rounded-2xl px-4 py-3"
                    />

                  </div>

                ))}

              </div>

            </div>

            {/* PAYMENT DETAILS */}

            <div className="mb-8">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">

                <h2 className="text-xl sm:text-2xl font-semibold text-slate-800">
                  Payment Details
                </h2>

                <button
                  type="button"
                  onClick={addPaymentDetailRow}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-2xl text-sm font-medium"
                >
                  + Add Payment
                </button>

              </div>

              <div className="space-y-4">

                {payment.paymentDetails.length === 0 && (
                  <p className="text-sm text-slate-500">
                    No payment details added. Click &quot;+ Add Payment&quot; to add one.
                  </p>
                )}

                {payment.paymentDetails.map((row, index) => (

                  <div
                    key={index}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-3 p-4 bg-slate-50 border border-slate-200 rounded-3xl"
                  >

                    <div className="sm:col-span-12">
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        Description
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Advance payment of 10%"
                        value={row.description}
                        onChange={(e) =>
                          updatePaymentDetail(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3"
                      />
                    </div>

                    <div className="sm:col-span-3">
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        Amount (Rs.)
                      </label>
                      <input
                        type="text"
                        placeholder="2949"
                        value={row.amount}
                        onChange={(e) =>
                          updatePaymentDetail(
                            index,
                            "amount",
                            e.target.value
                          )
                        }
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3"
                      />
                    </div>

                    <div className="sm:col-span-3">
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        Date of Payment
                      </label>
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) =>
                          updatePaymentDetail(
                            index,
                            "date",
                            e.target.value
                          )
                        }
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3"
                      />
                    </div>

                    <div className="sm:col-span-5">
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        Reference Number
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 32456623456"
                        value={row.reference}
                        onChange={(e) =>
                          updatePaymentDetail(
                            index,
                            "reference",
                            e.target.value
                          )
                        }
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3"
                      />
                    </div>

                    <div className="sm:col-span-1 flex items-end">
                      <button
                        type="button"
                        onClick={() => removePaymentDetailRow(index)}
                        className="w-full h-12 rounded-2xl bg-red-50 hover:bg-red-100 text-red-500 font-bold text-xl"
                      >
                        ×
                      </button>
                    </div>

                  </div>

                ))}

              </div>

            </div>

            {/* ADDITIONAL INFO */}

            <div className="mb-8">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">

                <h2 className="text-xl sm:text-2xl font-semibold text-slate-800">
                  Additional Information
                </h2>

                <button
                  type="button"
                  onClick={addAdditionalRow}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-2xl text-sm font-medium"
                >
                  + Add Row
                </button>

              </div>

              <div className="space-y-3">

                {payment.additionalInfo.length === 0 && (
                  <p className="text-sm text-slate-500">
                    No additional information added. Click &quot;+ Add Row&quot; to add one.
                  </p>
                )}

                {payment.additionalInfo.map(
                  (row, index) => (

                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-12 gap-3"
                    >

                      <input
                        type="text"
                        placeholder="Key"
                        value={row.key}
                        onChange={(e) =>
                          updateAdditional(
                            index,
                            "key",
                            e.target.value
                          )
                        }
                        className="sm:col-span-5 border border-slate-300 rounded-2xl px-4 py-3"
                      />

                      <input
                        type="text"
                        placeholder="Value"
                        value={row.value}
                        onChange={(e) =>
                          updateAdditional(
                            index,
                            "value",
                            e.target.value
                          )
                        }
                        className="sm:col-span-6 border border-slate-300 rounded-2xl px-4 py-3"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          removeAdditionalRow(
                            index
                          )
                        }
                        className="sm:col-span-1 h-12 rounded-2xl bg-red-50 hover:bg-red-100 text-red-500 font-bold text-xl"
                      >
                        ×
                      </button>

                    </div>

                  )
                )}

              </div>

            </div>

            {/* PRODUCTS */}

            <div className="mb-8">

              <ProductSelector
                products={productsData}
                selectedItems={items}
                setSelectedItems={setItems}
              /> 

            </div>

            {/* TOTALS */}

            <div className="bg-slate-50 rounded-3xl border border-slate-200 p-5 sm:p-6">

              <div className="space-y-4">

                <div className="flex justify-between">
                  <span>Amount</span>

                  <span>
                    ₹{" "}
                    {totals.amount.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Discount</span>

                  <span>
                    ₹{" "}
                    {totals.discount.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Taxable Amount</span>

                  <span>
                    ₹{" "}
                    {totals.taxableAmount.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                </div>

                {taxMode === "igst" ? (

                  <div className="flex justify-between">

                    <span>
                      IGST (18%)
                    </span>

                    <span>
                      ₹{" "}
                      {totals.igst.toLocaleString(
                        "en-IN",
                        { maximumFractionDigits: 0 }
                      )}
                    </span>

                  </div>

                ) : (

                  <>
                    <div className="flex justify-between">

                      <span>
                        CGST (9%)
                      </span>

                      <span>
                        ₹{" "}
                        {totals.cgst.toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 0 }
                        )}
                      </span>

                    </div>

                    <div className="flex justify-between">

                      <span>
                        SGST (9%)
                      </span>

                      <span>
                        ₹{" "}
                        {totals.sgst.toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 0 }
                        )}
                      </span>

                    </div>
                  </>

                )}

                <div className="border-t pt-4 flex justify-between text-lg sm:text-2xl font-bold text-slate-800">

                  <span>
                    Total (INR)
                  </span>

                  <span>
                    ₹{" "}
                    {totals.total.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>

                </div>

              </div>

            </div>

          </div>

          {/* PREVIEW */}

          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-4 sm:p-6 h-fit 2xl:sticky 2xl:top-6">

            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-5">
              Live Preview
            </h2>

            <div className="overflow-auto">

              <InvoicePreview
                documentType={documentType}
                invoiceNo={invoiceNo}
                prnNo={prnNo}
                invoiceDate={invoiceDate}
                customer={customer}
                items={calculatedItems}
                totals={totals}
                payment={payment}
                taxMode={taxMode}
              />

            </div>

          </div>

        </div>

      </div>
    </div>
  );
}